import React from 'react';
import styled from 'styled-components';
import { Body5 } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { DATA_ANALYST_SOURCES } from '@constants/dataAnalystAgentSources';
import { COPY as FEED_INPUT_COPY, describeRows } from './feedInputs';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;
const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const ChipsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
`;
const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  color: ${({ theme }) => theme.themeColors.text};
  white-space: nowrap;
`;

const sourceLabel = (id) => {
  const found = DATA_ANALYST_SOURCES.find((s) => s.id === id);
  return found ? found.label : id;
};

const formatResumedDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const SessionSummaryCard = ({
  selectedCsvDocs = [],
  selectedFeedInputs = [],
  noRetrieval = false,
  onlySources = [],
  excludeSources = [],
  onStartNewSession,
  disabled = false,
  isResumed = false,
  resumedFromId = null,
  resumedFromCreatedAt = null,
}) => {
  const hasCsvs = selectedCsvDocs.length > 0;
  const hasFeeds = selectedFeedInputs.length > 0;
  const hasOnly = onlySources.length > 0;
  const hasExclude = excludeSources.length > 0;

  if (isResumed) {
    const formattedDate = formatResumedDate(resumedFromCreatedAt);
    return (
      <Card>
        <HeaderRow>
          <Body5 bold>Session Context</Body5>
          <TransparentFlatButton onClick={onStartNewSession} disabled={disabled}>
            Start New Session
          </TransparentFlatButton>
        </HeaderRow>
        <Section>
          <Body5 isLowContrast>
            Resumed from session #{resumedFromId}
            {formattedDate ? ` · originally run ${formattedDate}` : ''}
          </Body5>
        </Section>
      </Card>
    );
  }

  return (
    <Card>
      <HeaderRow>
        <Body5 bold>Session Context</Body5>
        <TransparentFlatButton onClick={onStartNewSession} disabled={disabled}>
          Start New Session
        </TransparentFlatButton>
      </HeaderRow>

      <Section>
        <Body5 bold>CSV Data</Body5>
        {hasCsvs ? (
          <ChipsRow>
            {selectedCsvDocs.map((doc) => (
              <Chip key={doc.id}>
                <Body5>{doc.filename || `Doc ${doc.id}`}</Body5>
              </Chip>
            ))}
          </ChipsRow>
        ) : (
          <Body5 isLowContrast>None</Body5>
        )}
      </Section>

      {/* Label and row count only. The rows themselves were released on kickoff, and five
          25,000-row feeds are not something a summary should try to draw anyway. */}
      <Section>
        <Body5 bold>{FEED_INPUT_COPY.summaryLabel}</Body5>
        {hasFeeds ? (
          <ChipsRow>
            {selectedFeedInputs.map((item) => (
              <Chip key={item.id}>
                <Body5>{`${item.id} · ${describeRows(item.entry?.row_count || 0)}`}</Body5>
              </Chip>
            ))}
          </ChipsRow>
        ) : (
          <Body5 isLowContrast>{FEED_INPUT_COPY.summaryNone}</Body5>
        )}
      </Section>

      <Section>
        <Body5 bold>Retrieval</Body5>
        <Body5 isLowContrast>{noRetrieval ? 'Off (attached data only)' : 'On'}</Body5>
      </Section>

      {hasOnly ? (
        <Section>
          <Body5 bold>Include only</Body5>
          <ChipsRow>
            {onlySources.map((id) => (
              <Chip key={id}>
                <Body5>{sourceLabel(id)}</Body5>
              </Chip>
            ))}
          </ChipsRow>
        </Section>
      ) : null}

      {hasExclude ? (
        <Section>
          <Body5 bold>Exclude</Body5>
          <ChipsRow>
            {excludeSources.map((id) => (
              <Chip key={id}>
                <Body5>{sourceLabel(id)}</Body5>
              </Chip>
            ))}
          </ChipsRow>
        </Section>
      ) : null}
    </Card>
  );
};
